import { useAuth } from '@/contexts/AuthContext';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer, PageHeader } from '@/components/ui';

import { AgendaHojeWidget } from './widgets/AgendaHojeWidget';
import { AtividadesHojeWidget } from './widgets/AtividadesHojeWidget';
import { KpisRealsWidget } from './widgets/KpisRealsWidget';
// CARD-VENDAS-VENDEDOR (21/08): "Minhas vendas" do mês — só pra quem tem
// vendedor do Omie vinculado (Gabriel/Enzo); sem vínculo o widget é null.
import { MinhasVendasWidget } from './widgets/MinhasVendasWidget';
import { ItensParadosWidget } from './widgets/ItensParadosWidget';
import { StatusReautorizacaoBanner } from './widgets/StatusReautorizacaoBanner';
// FASE 4 / M3 (auditoria 2026-05-21): widget consumindo
// GET /api/v1/task-approvals/me/pending (endpoint backend antes sem UI).
// Auto-hide quando lista vazia — não polui Home pra users sem pendências.
import { PendingApprovalsWidget } from '@/components/PendingApprovalsWidget';
// R6.5 / FRENTE C: banner "Instalar IGM como app" (Android/Chrome) ou
// instruções iOS Safari ("Adicionar à Tela de Início"). Auto-hide se já
// instalado ou dismissado nessa sessão.
import { InstallPwaBanner } from '@/components/InstallPwaBanner';
// O18 (Orçamento v2): home ESPECÍFICO do colaborador do dept comercial —
// metas escolhíveis, agenda com horário, alertas e pipeline (sem OS/preventivas).
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { HomeComercial } from './HomeComercial';
// Bloco E (mig 379): home do departamento orçamentista (fila de orçamentos).
import { HomeOrcamentista } from './HomeOrcamentista';
// PF4 (rodada PIPELINE-FLUXO): decisão de qual home mostrar (extraída pra teste).
import { escolherHomeVariant } from './home-routing';
import { WidgetsPersonalizadosSection } from './widgets/WidgetsPersonalizadosSection';

import { formatarData, horaEmBrasilia } from '@/lib/format-date';
/**
 * Home — painel real do dia (R8: substituiu placeholders R0).
 *
 * Estrutura:
 *   1. Saudação personalizada (mantida da R0)
 *   2. StatusReautorizacaoBanner (oculto se scope OK)
 *   3. KpisRealsWidget (4 cards reais via /api/v1/kpis/today)
 *   4. Grid lado-a-lado em desktop, stacked em mobile:
 *      - AgendaHojeWidget (Google Calendar via /calendar/today)
 *      - AtividadesHojeWidget (deals + OSs via /activities/today)
 *
 * (B2 Lote 4 #11: seção "Acesso rápido" removida — navegação é pela sidebar.)
 *
 * Refresh dos 3 widgets: 5 min via React Query refetchInterval.
 * Empty states bonitos por widget.
 *
 * Layout ocupa 100% da largura (regra global IGM_App_v2).
 */
export function HomePage() {
  const { user } = useAuth();
  const crmV2On = useMyFeatureFlag('CRM_V2_ENABLED');

  // O18 / PF4 — ESCOPO BLINDADO: os homes de dept (comercial/orçamentista) são
  // SÓ pra colaborador (não-admin) do respectivo departamento, com o CRM v2
  // ligado. Todos os demais perfis (Financeiro, Projetos, Técnico, admin…)
  // mantêm o home clássico abaixo. Comercial vence orçamentista (dept duplo).
  const homeVariant = escolherHomeVariant(user, crmV2On);
  if (homeVariant === 'comercial') {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: os três wrappers `max-w-[1200px] px-4 py-6 md:px-8
            md:py-10` desta tela (comercial, orçamentista e clássico) viraram a
            régua única. */}
        <PageContainer>
          <HomeComercial />
        </PageContainer>
      </AppShell>
    );
  }

  // Bloco E (mig 379): home do orçamentista — fila filtrada dos cards na
  // fase-gatilho. Departamento próprio (o admin vê pela aba Indicadores).
  if (homeVariant === 'orcamentista') {
    return (
      <AppShell>
        <PageContainer>
          <HomeOrcamentista />
        </PageContainer>
      </AppShell>
    );
  }

  // #484: o relógio de Brasília, não o do computador.
  const hora = horaEmBrasilia().hora;
  const periodo = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
  const nome = user?.name?.split(' ')[0] || 'Matheus';

  const hoje = formatarData(new Date(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).replace(/^./, (c) => c.toUpperCase());

  return (
    <AppShell>
      <PageContainer>
        {/* FASE-C: saudação pelo PageHeader canônico (a data virou o crumb; o
            h1 de 60px caía pra fora da escala e empurrava os KPIs pra baixo
            da dobra no celular). */}
        <PageHeader
          crumb={hoje}
          title={`${periodo}, ${nome}`}
          subtitle="O resumo do seu dia: números de agora, o que está na agenda e o que precisa de você."
        />

        {/* Banner de re-autorização (oculto quando scope OK) */}
        <StatusReautorizacaoBanner />

        {/* R6.5: banner de instalação PWA (oculto se já instalado/dismissado) */}
        <div className="mb-4">
          <InstallPwaBanner />
        </div>

        {/* CARD-VENDAS-VENDEDOR: o número pessoal vem ANTES dos números do
            sistema; some sozinho pra quem não tem vínculo. */}
        <MinhasVendasWidget />

        {/* D71: os itens parados da Manutenção — só para quem o servidor diz que vê; para os outros, nada. */}
        <ItensParadosWidget />

        {/* KPIs reais — substituem placeholders R0 */}
        <div className="mb-6">
          <KpisRealsWidget />
        </div>

        {/* Grid: Agenda + Atividades */}
        <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AgendaHojeWidget />
          <AtividadesHojeWidget />
        </section>

        {/* FASE 4 / M3: aprovações pendentes (auto-hide se vazio) */}
        <PendingApprovalsWidget />

        {/* 07/08/2026 — a metade que LÊ o "Personalizar Home" (MAPA §6.12).
            O builder gravava em `user_dashboard_layouts` e esta tela montava
            widgets fixos por import, sem nunca ler o que foi salvo: a função
            existia pela metade desde a migration 128.

            Entra ADITIVO, no fim: quem nunca usou o builder não vê nada, e
            quem salvou um layout não perde o que já tinha em cima. */}
        <WidgetsPersonalizadosSection />

        {/*
          B2 Lote 4 #11: seção "Acesso rápido" removida — a navegação principal
          é pela sidebar (e os atalhos duplicavam, alguns pra rotas mortas como
          /menu/compras e /menu/rh). Mantém saudação + KPIs + Agenda/Atividades.
        */}
      </PageContainer>
    </AppShell>
  );
}
